import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, ArrowLeft, ArrowRight, Share2, Facebook, Twitter, Linkedin } from 'lucide-react';
import { apiRequest, formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import AppointmentModal from '../components/AppointmentModal';
import DOMPurify from 'dompurify';

// Full blog content
const BLOG_CONTENT = {
  'what-is-a-denturist': {
    title: 'What Is a Denturist? (And How Is It Different from a Dentist?)',
    content: `
      <p>A Denturist is a licensed oral health professional who specializes exclusively in the design, fabrication, fitting, and maintenance of removable dental prosthetics such as full and partial dentures.</p>
      
      <h2>The Key Difference</h2>
      <p>Unlike a general dentist, a Denturist focuses entirely on dentures and oral appliances. Patients in Coos Bay, North Bend, Bandon, Florence, Roseburg, and surrounding Oregon communities increasingly choose direct-to-denturist care because:</p>
      
      <ul>
        <li>You work directly with the professional creating your dentures</li>
        <li>No referral chain</li>
        <li>Fewer appointments</li>
        <li>Lower overall cost</li>
        <li>Personalized adjustments</li>
      </ul>
      
      <h2>How the Process Works</h2>
      <p>Dentists often refer denture fabrication to third-party labs. A Denturist performs impressions, fabrication, fittings, and adjustments personally. This direct approach means better communication, faster turnaround, and often significant cost savings.</p>
      
      <h2>Important Note</h2>
      <p>We do not perform extractions or implants. If extractions are required, your dentist performs them, and we begin denture fabrication coordination.</p>
      
      <p>Schedule your consultation today in Coos Bay or North Bend to learn how direct denturist care can benefit you.</p>
    `,
    featured_image: 'https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=800',
    created_at: '2024-01-15T10:00:00Z'
  },
  'insurance-supports-direct-care': {
    title: 'Why Insurance Companies Support Direct Denturist Care',
    content: `
      <p>Insurance carriers increasingly recognize that direct denturist care reduces unnecessary layered costs.</p>
      
      <h2>The Traditional Path</h2>
      <p>Dentist → Specialist → Lab → Patient</p>
      <p>Each step adds overhead, fees, and delays to the process.</p>
      
      <h2>The Direct Denturist Path</h2>
      <p>Consultation → Fabrication → Fitting → Maintenance</p>
      <p>One provider handles everything, streamlining the process.</p>
      
      <h2>Cost Benefits</h2>
      <p>By eliminating referral redundancies and lab markups, treatment costs are often reduced. This efficiency benefits patients and insurers alike.</p>
      
      <p>Communities served include Coos Bay, Florence, Bandon, Reedsport, and Roseburg.</p>
      
      <p>Request a cost comparison consultation today.</p>
    `,
    featured_image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800',
    created_at: '2024-01-12T10:00:00Z'
  },
  'cost-savings-denturist': {
    title: 'The True Cost Savings of Choosing a Denturist',
    content: `
      <p>Patients frequently ask why pricing differs between general dental offices and denturist practices.</p>
      
      <h2>Why Costs Are Different</h2>
      <p>Cost differences stem from:</p>
      <ul>
        <li>Fewer intermediaries</li>
        <li>Direct fabrication</li>
        <li>Specialized focus</li>
        <li>Reduced overhead</li>
      </ul>
      
      <h2>Direct Care Advantage</h2>
      <p>By going direct, patients in Coos County often experience meaningful savings while receiving specialized expertise. You're paying for the dentures and the professional service—not for referral fees and multiple facility markups.</p>
      
      <p>Call today for transparent pricing.</p>
    `,
    featured_image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800',
    created_at: '2024-01-10T10:00:00Z'
  },
  'dentures-restore-confidence': {
    title: 'How Dentures Restore Confidence and Oral Function',
    content: `
      <p>Well-fitted dentures restore so much more than just appearance.</p>
      
      <h2>What Dentures Restore</h2>
      <ul>
        <li>Chewing efficiency</li>
        <li>Speech clarity</li>
        <li>Facial structure support</li>
        <li>Nutritional health</li>
        <li>Self-confidence</li>
      </ul>
      
      <h2>The Importance of Proper Fit</h2>
      <p>Poorly fitted dentures may lead to gum irritation, sore spots, and digestive compromise. That's why working with a specialist who can make precise adjustments is so important.</p>
      
      <p>Our practice serves Coos Bay, North Bend, Coquille, Myrtle Point, Brookings, and Florence.</p>
      
      <p>Schedule a denture evaluation today.</p>
    `,
    featured_image: 'https://images.unsplash.com/photo-1580483046931-aaba29b81601?w=800',
    created_at: '2024-01-08T10:00:00Z'
  },
  'denture-process-step-by-step': {
    title: 'The Denture Process Step-by-Step',
    content: `
      <p>Understanding what to expect can help ease anxiety about getting dentures.</p>
      
      <h2>The Process</h2>
      <ol>
        <li><strong>Consultation</strong> - We assess your needs and discuss options</li>
        <li><strong>Oral Assessment</strong> - Detailed examination of your mouth</li>
        <li><strong>Impressions</strong> - Creating precise molds of your mouth</li>
        <li><strong>Fabrication</strong> - Crafting your custom dentures</li>
        <li><strong>Fitting</strong> - Ensuring proper fit and comfort</li>
        <li><strong>Adjustments</strong> - Fine-tuning for optimal function</li>
        <li><strong>Ongoing Care</strong> - Maintenance and support</li>
      </ol>
      
      <h2>Immediate Dentures</h2>
      <p>Immediate dentures can be fabricated prior to extraction by your dentist, so you never go without teeth during healing.</p>
      
      <p>Book your consultation today.</p>
    `,
    featured_image: 'https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?w=800',
    created_at: '2024-01-06T10:00:00Z'
  },
  'common-denture-myths': {
    title: 'Common Myths About Dentures',
    content: `
      <p>Let's address some common misconceptions about modern dentures.</p>
      
      <h2>Myth: Dentures look fake</h2>
      <p><strong>Reality:</strong> Modern materials and techniques create dentures with natural aesthetics. Today's dentures are designed to look like natural teeth.</p>
      
      <h2>Myth: Dentures are always uncomfortable</h2>
      <p><strong>Reality:</strong> Properly fitted dentures should be comfortable. Discomfort usually indicates a need for adjustment or relining.</p>
      
      <h2>Myth: Dentures are only for elderly patients</h2>
      <p><strong>Reality:</strong> Patients of many ages require dentures. Tooth loss can occur due to various factors including genetics, injury, and health conditions.</p>
      
      <h2>Myth: You can't eat normally with dentures</h2>
      <p><strong>Reality:</strong> With properly fitted dentures and some practice, most patients can enjoy their favorite foods.</p>
      
      <p>Learn how modern dentures differ—schedule a consultation today.</p>
    `,
    featured_image: 'https://images.unsplash.com/photo-1606811841689-23dfddce3e95?w=800',
    created_at: '2024-01-04T10:00:00Z'
  },
  'why-oregon-patients-trust-us': {
    title: 'Why Oregon Patients Trust Our Practice',
    content: `
      <p>As Oregon's longest-standing denturist practice serving Coos County, our longevity reflects:</p>
      
      <ul>
        <li>Quality materials</li>
        <li>Precision craftsmanship</li>
        <li>Ethical standards</li>
        <li>Community trust</li>
      </ul>
      
      <h2>Our Commitment</h2>
      <p>We take personal responsibility for each case. From initial consultation to final fitting and beyond, you work directly with experienced professionals who care about your results.</p>
      
      <h2>Community Roots</h2>
      <p>We've been part of the Oregon Coast community for decades, building relationships and trust with families across generations.</p>
      
      <p>Schedule a consultation today and join the thousands of patients who trust us with their smiles.</p>
    `,
    featured_image: 'https://images.pexels.com/photos/35438289/pexels-photo-35438289.jpeg?w=800',
    created_at: '2024-01-02T10:00:00Z'
  },
  'when-to-replace-reline-dentures': {
    title: 'When to Replace or Reline Dentures',
    content: `
      <p>Dentures don't last forever. Here are signs you may need relining or replacement.</p>
      
      <h2>Signs to Watch For</h2>
      <ul>
        <li><strong>Slipping</strong> - Dentures that move around during eating or speaking</li>
        <li><strong>Sore spots</strong> - Irritation or pain in your gums</li>
        <li><strong>Difficulty chewing</strong> - Food seems harder to bite and chew</li>
        <li><strong>Changes in speech</strong> - Lisping or difficulty with certain sounds</li>
        <li><strong>Gum shrinkage</strong> - Natural changes in your gum tissue over time</li>
      </ul>
      
      <h2>Reline vs. Replace</h2>
      <p>Sometimes a reline can restore fit and comfort without needing new dentures. We'll assess your situation and recommend the best solution.</p>
      
      <p>Residents of Coos Bay, Florence, Roseburg, and Brookings should schedule periodic evaluations to maintain optimal denture function.</p>
      
      <p>Call for an assessment today.</p>
    `,
    featured_image: 'https://images.unsplash.com/photo-1598256989800-fe5f95da9787?w=800',
    created_at: '2024-01-01T10:00:00Z'
  },
};

export default function BlogDetailPage() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAppointment, setShowAppointment] = useState(false);

  useEffect(() => {
    // Try API first, fall back to static content
    apiRequest(`/public/blogs/${slug}`)
      .then(setBlog)
      .catch(() => {
        // Use static content if API fails
        if (BLOG_CONTENT[slug]) {
          setBlog({
            ...BLOG_CONTENT[slug],
            slug,
            id: slug
          });
        }
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="pt-24 lg:pt-32 min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-orange-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="pt-24 lg:pt-32 min-h-screen" data-testid="blog-not-found">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
          <h1 className="text-3xl font-bold text-slate-900">Article Not Found</h1>
          <p className="mt-4 text-slate-600">The article you're looking for doesn't exist.</p>
          <Link to="/blog">
            <Button className="mt-8 bg-orange-600 hover:bg-orange-700 rounded-full">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const staticContent = BLOG_CONTENT[slug];

  return (
    <div className="pt-24 lg:pt-32" data-testid="blog-detail-page">
      {/* Hero */}
      <section className="py-12 lg:py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/blog" className="inline-flex items-center text-slate-600 hover:text-orange-600 transition-colors mb-8">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Link>
          <div className="flex items-center gap-3 text-sm text-slate-500 mb-4">
            <Calendar className="w-4 h-4" />
            <span>{formatDate(blog.created_at)}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            {blog.title}
          </h1>
        </div>
      </section>

      {/* Featured Image */}
      {(blog.featured_image || (staticContent && staticContent.featured_image)) && (
        <section className="bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
            <img
              src={blog.featured_image || staticContent?.featured_image}
              alt={blog.title}
              className="w-full rounded-2xl shadow-xl aspect-[21/9] object-cover"
            />
          </div>
        </section>
      )}

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <article 
            className="prose prose-lg prose-slate max-w-none prose-headings:font-serif prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4 prose-p:leading-relaxed prose-li:leading-relaxed prose-a:text-orange-600 prose-a:no-underline hover:prose-a:underline"
            dangerouslySetInnerHTML={{ 
              __html: DOMPurify.sanitize(staticContent?.content || blog.content || '', {
                ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'a', 'span', 'div', 'img', 'blockquote', 'code', 'pre'],
                ALLOWED_ATTR: ['href', 'src', 'alt', 'style', 'class', 'target']
              })
            }}
          />

          {/* Share */}
          <div className="mt-12 pt-8 border-t">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">Share this article</span>
              <div className="flex items-center gap-3">
                <button className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 hover:bg-blue-100 hover:text-blue-600 transition-colors">
                  <Facebook className="w-5 h-5" />
                </button>
                <button className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 hover:bg-sky-100 hover:text-sky-600 transition-colors">
                  <Twitter className="w-5 h-5" />
                </button>
                <button className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 hover:bg-blue-100 hover:text-blue-700 transition-colors">
                  <Linkedin className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-12 bg-orange-50 rounded-2xl p-8 text-center">
            <h3 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Ready to Learn More?
            </h3>
            <p className="mt-2 text-slate-600">
              Schedule a consultation to discuss your denture needs with our experts.
            </p>
            <Button
              onClick={() => setShowAppointment(true)}
              className="mt-6 bg-orange-600 hover:bg-orange-700 rounded-full px-8"
              data-testid="blog-detail-schedule-btn"
            >
              Schedule Consultation
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      <AppointmentModal open={showAppointment} onOpenChange={setShowAppointment} />
    </div>
  );
}
